import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { t } from 'i18next'
import api from '@/core/services/api'
import { useCommonState } from '@/core/context/useContext'
import { getBaseCurrency } from '@/constant/currency'
import tabImg from '@/assets/image/gold/recent_tab.png'
import upImg from '@/assets/image/gold/recent_up.png'
import downImg from '@/assets/image/gold/recent_down.png'

/**
 * 화면 아래 「歴史」 서랍 — gold8.bet PC(.recentView) 실측:
 *   - 바: 화면 폭 전체, 높이 77px, #000c, 닫히면 bottom:-77px 로 내려간다(0.4초).
 *   - 안쪽은 가운데 1200px 본문 폭. 탭(150×24, tab.png)은 그 본문 폭의 왼쪽 위에 붙는다.
 *   - 왼쪽 「歴史」 제목(17px) + 오른쪽 세로줄, 그 옆에 최근 플레이한 게임 썸네일(60×60).
 *     마우스를 올리면 빨간 말풍선에 게임 이름, 누르면 그 게임 실행.
 *     말풍선은 게임 이름 전체가 보이게 글자 길이만큼 넓어진다(gold 의 90px 고정·말줄임은 쓰지 않음).
 *     가로 스크롤 목록 안에 두면 양 끝 게임의 말풍선이 잘려서 body 에 띄우고, 화면 밖으로 나가지 않게 좌우를 맞춘다.
 *   데이터: 로그인 회원의 최근 베팅 게임(/user-game/recent-bet) — 서랍을 열 때마다 새로 받는다, 최신이 왼쪽.
 *   기존 기능인 베팅내역(/bet-history) 이동 버튼은 오른쪽에 그대로 둔다.
 */
const H = 77

const Root = styled.div`
  position: fixed;
  left: 0;
  bottom: ${({ $open }) => ($open ? 0 : `-${H}px`)};
  width: 100%;
  height: ${H}px;
  background: rgba(0, 0, 0, 0.8);
  font-size: 14px;
  color: #fff;
  transition: bottom 0.4s;
  z-index: 25;
`

const Wrapper = styled.div`
  position: relative;
  width: 1200px;
  max-width: 100%;
  height: ${H}px;
  margin: 0 auto;
  padding-left: 80px;
`

const Tab = styled.button`
  position: absolute;
  top: -24px;
  left: 0;
  width: 150px;
  height: 24px;
  padding: 0;
  border: 0;
  background: url(${tabImg}) no-repeat;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: #fff;
  cursor: pointer;
  &::after {
    content: '';
    display: inline-block;
    width: 7px;
    height: 8px;
    margin-left: 5px;
    background: url(${({ $open }) => ($open ? downImg : upImg)}) no-repeat;
  }
`

const Title = styled.span`
  position: absolute;
  top: 15px;
  left: 19px;
  width: 50px;
  height: 48px;
  display: flex;
  align-items: center;
  font-size: 17px;
  color: #fff;
`

const Box = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  padding: 0 15px;
`

const ListWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
`

const List = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 4px;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`

const Item = styled.button`
  position: relative;
  flex: 0 0 60px;
  width: 60px;
  height: 60px;
  padding: 0;
  border: 0;
  background: #111;
  cursor: pointer;
  img { display: block; width: 60px; height: 60px; object-fit: cover; }
`

/* 게임 칸 위 6px 에 아래쪽 끝을 맞춘다(left·top·--arrow-x 는 띄울 때 계산). 화면 폭보다 긴 이름만 줄을 바꾼다 */
const Tip = styled.span`
  position: fixed;
  z-index: 1000;
  width: max-content;
  max-width: calc(100vw - 16px);
  padding: 4px 8px;
  background: #cd0030;
  font-size: 12px;
  line-height: 17px;
  color: #fff;
  text-align: center;
  white-space: normal;
  transform: translateY(-100%);
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
    left: var(--arrow-x, 50%);
    bottom: -4px;
    transform: translateX(-50%);
    border-style: solid;
    border-width: 4px 4px 0 4px;
    border-color: #cd0030 transparent transparent transparent;
  }
`

const Nav = styled.button`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === 'prev' ? 'left: -14px;' : 'right: -14px;')}
  transform: translateY(-50%);
  width: 20px;
  height: 40px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 20px;
  color: #fff;
  opacity: 0.75;
  cursor: pointer;
  &:hover { opacity: 1; }
`

const NoData = styled.span`
  display: inline-block;
  margin: 0 8px;
  color: #fff;
`

const HistoryBtn = styled.button`
  flex: 0 0 auto;
  height: 30px;
  padding: 0 14px;
  border: 1px solid #fff7b5;
  border-radius: 12px;
  background: transparent;
  font-size: 13px;
  color: #fff7b5;
  cursor: pointer;
`

export default function GoldRecentView() {
  const [open, setOpen] = useState(false)
  const [games, setGames] = useState([])
  const [overflow, setOverflow] = useState(false)
  const [tip, setTip] = useState(null) // { title, cx(게임 칸 가운데 x), top(게임 칸 위 y) }
  const listRef = useRef(null)
  const tipRef = useRef(null)
  const { isLogin } = useCommonState()
  const navigate = useNavigate()

  const showTip = (el, title) => {
    if (!title) return
    const r = el.getBoundingClientRect()
    setTip({ title, cx: r.left + r.width / 2, top: r.top - 6 })
  }
  const hideTip = () => setTip(null)

  // 말풍선 폭을 잰 뒤 화면 좌우 8px 안으로 맞추고, 꼬리는 게임 칸 가운데를 가리키게 둔다
  useLayoutEffect(() => {
    const el = tipRef.current
    if (!tip || !el) return
    const w = el.offsetWidth
    const left = Math.min(Math.max(tip.cx - w / 2, 8), window.innerWidth - 8 - w)
    el.style.left = `${left}px`
    el.style.setProperty('--arrow-x', `${tip.cx - left}px`)
  }, [tip])

  // 서랍이 닫히면 말풍선도 닫는다
  useEffect(() => { if (!open) setTip(null) }, [open])

  // 서랍을 열 때마다 최근 게임을 새로 받는다(방금 한 게임도 바로 보이게)
  useEffect(() => {
    if (!open || !isLogin) return
    api.get('/user-game/recent-bet', { params: { page: 1, countPerPage: 30 } })
      .then((res) => {
        const list = Array.isArray(res.data?.content) ? res.data.content : []
        // API 는 오래된 순 — 최신이 왼쪽에 오게 뒤집는다
        setGames([...list].reverse())
      })
      .catch(console.error)
  }, [open, isLogin])

  useEffect(() => {
    const el = listRef.current
    setOverflow(!!el && el.scrollWidth > el.clientWidth + 2)
  }, [games, open])

  const scroll = (dir) => listRef.current?.scrollBy({ left: dir * 340, behavior: 'smooth' })

  const play = (g) => {
    if (!g?.vendor || !g?.id) return
    navigate(`/game-play/${g.vendor}/${g.id}/${getBaseCurrency()}`)
  }

  return (
    <Root $open={open}>
      <Wrapper>
        <Tab type="button" $open={open} onClick={() => setOpen(v => !v)}>
          {open ? t('gold.collapse') : t('footer.historyTitle')}
        </Tab>
        <Title>{t('footer.historyTitle')}</Title>
        <Box>
          <ListWrap>
            {isLogin && games.length > 0 ? (
              <>
                {overflow && <Nav type="button" $side="prev" aria-label="prev" onClick={() => scroll(-1)}>‹</Nav>}
                <List ref={listRef} onScroll={hideTip}>
                  {games.map((g) => (
                    <Item
                      key={g.no}
                      type="button"
                      onClick={() => play(g)}
                      onMouseEnter={(e) => showTip(e.currentTarget, g.title)}
                      onMouseLeave={hideTip}
                      onFocus={(e) => showTip(e.currentTarget, g.title)}
                      onBlur={hideTip}
                      aria-label={g.title}
                    >
                      <img src={g.thumbnail} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
                    </Item>
                  ))}
                </List>
                {overflow && <Nav type="button" $side="next" aria-label="next" onClick={() => scroll(1)}>›</Nav>}
              </>
            ) : (
              <NoData>{t('gold.noData')}</NoData>
            )}
          </ListWrap>
          {isLogin && <HistoryBtn type="button" onClick={() => navigate('/bet-history')}>{t('gold.betHistory')}</HistoryBtn>}
        </Box>
      </Wrapper>
      {tip && createPortal(
        <Tip ref={tipRef} role="tooltip" style={{ left: tip.cx, top: tip.top }}>{tip.title}</Tip>,
        document.body,
      )}
    </Root>
  )
}
